import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, CircularProgress, Grid, IconButton, Paper, Typography } from '@mui/material';
import { addSeconds, format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { constants, remoteResourceLoadSuccess } from '../../helpers';
import { hasPermissions } from '../../helpers/utls';
import { AppPermissions } from '../../types';
import {
  activeCountriesSelector,
  changeAutoUpdateTimer,
  dashboardAutoUpdateTimerSelector,
  filterParametersDataSelector,
  findAllRequest,
  singleCountryDashboardSelector,
  userPermissionsDataSelector,
  dashboardCountrySelectedSelector,
} from '../../state';
import Countdown from './Countdown';
import Dashboards from './Dashboards';

export const MainPannel = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'end',
}));

export const PannelInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  opacity: 0.8,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginInline: theme.spacing(0.5),
}));

function getFormattedTime(time: number) {
  const result = Math.round((time - new Date().getTime()) / 1000);
  return result;
}

const DashboardData = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [initialDispatch, setInitialDispatch] = useState(false);

  const activeCountriesResource = useSelector(activeCountriesSelector);
  const permissionsData = useSelector(userPermissionsDataSelector);
  const autoUpdate = useSelector(dashboardAutoUpdateTimerSelector);
  const appParameters = useSelector(filterParametersDataSelector);
  const dashboardTimer = appParameters?.dashboardTimer || constants.defaultDashboardTimer;
  const singleCountryDashboard = useSelector(singleCountryDashboardSelector);
  const perms = useMemo(() => permissionsData?.map(i => i.permission) ?? [], [permissionsData]);
  const [formattedTime, setFormattedTime] = useState(getFormattedTime(autoUpdate.time));

  const hasFullCountryAccess = useMemo(() => {
    const dash = permissionsData?.find(
      p => p.permission === AppPermissions.CONCILIATIONS_DASHBOARD
    );
    return !!dash?.countries?.some(c => c.countryAcronym === 'FULL');
  }, [permissionsData]);

  const resources = Dashboards.map(i =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    i.resource ? useSelector(i.resource) : remoteResourceLoadSuccess([])
  );
  const loading = useMemo(() => resources.some(i => i.loading), [resources]);
  const loaded = useMemo(() => resources.every(i => i.loaded), [resources]);

  const updateTime = (updateState?: boolean) => {
    if (updateState) {
      dispatch(changeAutoUpdateTimer({ ...autoUpdate, time: autoUpdate.time - 1 }));
    } else {
      setFormattedTime(prev => prev - 1);
    }
  };

  const handleRefresh = () => {
    const newTime = new Date().getTime() + dashboardTimer * 60000;
    dispatch(changeAutoUpdateTimer({ time: newTime, lastUpdate: new Date() }));
    setFormattedTime(getFormattedTime(newTime));
    dispatch(findAllRequest());
  };

  const renderPannel = (title: string, value: string) => (
    <PannelInfo marginLeft={2} marginY={1}>
      <Typography variant="caption" sx={{ fontWeight: 500, marginTop: '1px' }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ marginLeft: 1, marginTop: '1px' }}>
        {value}
      </Typography>
    </PannelInfo>
  );

  const countdownTimer = (counter: number) => {
    const formattedTime = format(addSeconds(new Date(0), counter), 'mm:ss');
    return renderPannel(t('dashboard.nextUpdate'), formattedTime);
  };

  useEffect(() => {
    if (activeCountriesResource.loaded && !loaded && !initialDispatch) {
      dispatch(findAllRequest());
      setInitialDispatch(true);
    }
  }, [activeCountriesResource, dispatch, loaded, initialDispatch]);
  const activeCountry = useSelector(dashboardCountrySelectedSelector);
  const visible = useMemo(() => {
    if (!perms.length) return [];

    const ctx = { singleCountryDashboard, hasFullCountryAccess, activeCountry };

    const getOrder = (d: any) => (singleCountryDashboard ? d.singleCountryOrder : d.order);

    return Dashboards.filter(d => hasPermissions(perms, d.permissions))
      .flatMap(d => {
        const dashVisible = d.when ? d.when(ctx) : true;
        if (!dashVisible) return [];

        const dashOrder = getOrder(d);

        if (d.items?.length) {
          return d.items
            .filter(it => (it.when ? it.when(ctx) : true))
            .map((it, idx) => ({
              key: `${d.name}-${idx}`,
              gridProps: it.gridProps ?? d.gridProps,
              component: it.component,
              order: dashOrder,
            }));
        }

        if (!d.component) return [];
        return [{ key: d.name, gridProps: d.gridProps, component: d.component, order: dashOrder }];
      })
      .sort((a, b) => a.order - b.order);
  }, [perms, singleCountryDashboard, hasFullCountryAccess, activeCountry]);

  return (
    <Grid
      container
      rowSpacing={4}
      columnSpacing={3}
      justifyContent={{ xs: 'space-around', lg: 'flex-start' }}
    >
      <Grid item xs={12}>
        <MainPannel>
          <Box
            component={Paper}
            data-guide="dashboard-refresh-panel"
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              justifyContent: 'end',
            }}
          >
            {renderPannel(
              t('dashboard.lastUpdate'),
              format(autoUpdate.lastUpdate, 'yyyy-MM-dd HH:mm:ss')
            )}
            <Countdown
              time={formattedTime}
              updateTime={updateTime}
              onComplete={handleRefresh}
              render={countdownTimer}
            />
            <StyledIconButton
              aria-label="refresh"
              size="small"
              onClick={handleRefresh}
              disabled={loading}
            >
              {!loading && <RefreshIcon color="primary" />}
              {loading && <CircularProgress size={15} />}
            </StyledIconButton>
          </Box>
        </MainPannel>
      </Grid>

      {visible.map(x => (
        <Grid key={x.key} item {...x.gridProps} sx={{ order: x.order }}>
          {x.component}
        </Grid>
      ))}
    </Grid>
  );
};

export default DashboardData;
